import { TITLE_SUFFIX } from "config/routes";
import Head from "next/head";
import NProgress from "nprogress";
import { PropsWithChildren, useEffect } from "react";
import Navigation from "./components/Navigation";
import { GlobalStyles, Main, Wrapper, Version } from "./styles";
import { useCustomRouter } from "../CustomRouter";
import { AnimatePresence, useAnimation } from "framer-motion";
import { useAuth } from "@shared/Authentication";
import { driver } from "driver.js";
import { getRuntimeConfigOptional } from "lib/runtimeEnvClient";

const MAIN_VARIANTS = {
  initial: { opacity: 0 },
  enter: { opacity: 1 },
  exit: { opacity: 0 },
};

export default function Layout({ children }: PropsWithChildren<unknown>) {
  const { events, asPath } = useCustomRouter();
  const { user } = useAuth();
  const controls = useAnimation();

  // driver.js
  useEffect(() => {
    const hasSeenTour = localStorage.getItem("has_seen_tour");
    console.log("hasSeenTour", hasSeenTour);
    if (!hasSeenTour) {
      const d = driver({
        disableActiveInteraction: true,
        overlayClickBehavior: "nextStep",
        showProgress: true,
        steps: [
          {
            popover: {
              title: "Welcome to your Dashboard",
              description: `We've renamed "Admin" to <b>Dashboard</b> to better reflect the strategic scope of your work. 
              It goes beyond simple administration. Please also note our new URL: <a href="https://dashboard.reps.ai" target="_blank">dashboard.reps.ai</a>.`,
            },
          },
          {
            element: "#intelligence-nav-item",
            popover: {
              title: `"Reps" is now Intelligence`,
              description: `We've renamed this section because it offers more than just standard metrics. 
                This is your home for AI insights, themes, and deeper intelligence.`,
            },
          },
          {
            element: "#exchange-nav-item",
            popover: {
              title: `"Gallery" is now Exchange`,
              description: `We've updated the name to better reflect the reality: this is a living exchange of user activity, not just a static view.`,
            },
          },
          {
            element: "#builder-nav-item",
            popover: {
              title: ` "Plays" is now Builder`,
              description: `The "Plays" parent tab is now <b>Builder</b>. This is your central workspace for creating Plays and Reps.`,
              onPopoverRender: () => {
                document.getElementById("builder-nav-item")?.click();
              },
            },
          },
          {
            element: "#builder-nav-item",
            popover: {
              title: "See, Do, Share",
              description: `We've updated the terms to match user actions: 
              See, Do, and Share. 
              This language will be used across the User App, Dashboard, and Intelligence.
<ul>
  <li><b>Reps - See:</b> (Previously "Content")</li>
  <li><b>Reps - Do/Share:</b> (Previously "Practice")</li>
</ul>
`,
            },
          },
        ],
      });
      d.drive();
      localStorage.setItem("has_seen_tour", "true");
    }
  }, []);

  useEffect(() => {
    const onRouteChangeStart = () => {
      NProgress.start();
    };

    const onRouteChangeComplete = () => {
      NProgress.done();
    };

    const onRouteChangeError = () => {
      NProgress.done();
    };

    events.on("routeChangeStart", onRouteChangeStart);
    events.on("routeChangeComplete", onRouteChangeComplete);
    events.on("routeChangeError", onRouteChangeError);

    return () => {
      events.off("routeChangeStart", onRouteChangeStart);
      events.off("routeChangeComplete", onRouteChangeComplete);
      events.off("routeChangeError", onRouteChangeError);
    };
  }, [events]);

  useEffect(() => {
    void controls.start({ opacity: 1 });
  }, [controls]);

  return (
    <>
      <GlobalStyles />
      <Head>
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta charSet="utf-8" />

        <title>{TITLE_SUFFIX}</title>
        <meta name="description" content="BigSpring Admin" />
      </Head>
      <Wrapper
        $singleColumn={!user}
        initial={{ opacity: 0 }}
        animate={controls}
        transition={{ duration: 0.5 }}
      >
        {user && (
          <Navigation
            onOpenMenuClick={() =>
              controls.start({ gridTemplateColumns: "280px 1fr" })
            }
            onCloseMenuClick={() =>
              controls.start({ gridTemplateColumns: "40px 1fr" })
            }
          />
        )}
        <AnimatePresence exitBeforeEnter>
          <Main
            key={asPath}
            initial="initial"
            animate="enter"
            exit="exit"
            transition={{ duration: 0.15 }}
            variants={MAIN_VARIANTS}
          >
            {children}
            <Version>
              reps-dashboard@
              {getRuntimeConfigOptional("NEXT_PUBLIC_VERSION") ||
                "unknown-version"}
            </Version>
          </Main>
        </AnimatePresence>
      </Wrapper>
    </>
  );
}
